import type { ChartOfAccountCategory } from '../types'

/**
 * Finds the lowest-level ChartOfAccountCategory for a given account number and returns
 * both the category and the full breadcrumb chain from the root to that category.
 *
 * @param value - The account number to search for a category.
 * @param categories - The root list of ChartOfAccountCategory objects (can be nested).
 * @param chain - (Internal) Used for recursion to build the breadcrumb chain. Defaults to [].
 * @returns An object with:
 *   - category: The lowest-level ChartOfAccountCategory containing the value, or null if not found.
 *   - chain: The breadcrumb chain from root to the found category (empty if not found).
 */
export const getLowestLevelCategory = (
	code: string,
	categories: ChartOfAccountCategory[],
	chain: ChartOfAccountCategory[] = [],
): {
	category: ChartOfAccountCategory | null
	chain: ChartOfAccountCategory[]
} => {
	for (const category of categories) {
		if (
			category.rangeStart <= Number(code) &&
			Number(code) <= category.rangeEnd
		) {
			const newChain = [...chain, category]
			if (category.subCategories.length > 0) {
				const result = getLowestLevelCategory(
					code,
					category.subCategories,
					newChain,
				)
				if (result.category) return result
			}
			return { category, chain: newChain }
		}
	}
	return { category: null, chain }
}
